/**
 * Component <e2-store-alphabet-letter-group /> is used to display list of all stores groupped in alphabet order.
 * Stores are groupped by the city and added to letter group.
 *
 * @attr {array} preparedStores - array of all stores, get from redux store
 * @attr {number} amount-of-visible-stores-in-city - max number of stores for one city, shown in accordion group
 * @attr {boolean} is-simplify - Show only city name and store address and wrap it into link to store
 * @attr {boolean} do-api-on-first-update - Call api to get stores on first update
 * @attr {number} page-size - Set a value for stores displayed per page
 * @attr {array} sorting-fields - array of store fields that stores should be sorted by

 * description page
 * HTML Structure Example
 *
 * <e2-store-alphabet-letter-group></e2-store-alphabet-letter-group>
 */

import { html } from 'lit-element';

import { SimpleLitElement } from '@elab/components';
import connect from '@elab/core/store/connect';
import { selectors, actions as storeLocatorActions } from '@elab/core/store/store-locator/index';
import { selectors as uiSelectors } from '@elab/core/store/ui';
import { actions as globalActions } from '@elab/core/store/global';
import { getValueByStorePath } from '@elab/core/utils/helpers';

export class StoreAlphabetLetterGroup extends SimpleLitElement {
    static get properties() {
        return {
            preparedStores: {
                type: Array,
                attribute: 'prepared-stores',
            },
            amountOfVisibleStoresInCity: {
                type: Number,
                attribute: 'amount-of-visible-stores-in-city',
            },
            isSimplified: {
                type: Boolean,
                attribute: 'is-simplified',
            },
            storeLocatorUrl: {
                type: String,
                attribute: 'store-locator-url',
            },
            doAPIonFirstUpdate: {
                type: Boolean,
                attribute: 'do-api-on-first-update',
            },
            pageSize: {
                type: Number,
                attribute: 'page-size',
            },
            sortingFields: {
                type: Array,
                attribute: 'sorting-fields',
            },
            hideStoreCity: {
                type: Boolean,
                attribute: 'hide-store-city',
            },
            storesNotGroupedByCity: {
                type: Boolean,
                attribute: 'no-group-stores-by-city',
            },
            groupByStoreName: {
                type: Boolean,
                attribute: 'group-by-store-name',
            },
        };
    }

    constructor() {
        super();
        this.preparedStores = [];
        this.amountOfVisibleStoresInCity = 5;
        this.isSimplified = false;
        this.storeLocatorUrl = '/winkelzoeker?location=';
        this.pageSize = 10000; // HYBRIS set default value to 20
        this.sortingFields = [];
        this.hideStoreCity = false;
        this.storesNotGroupedByCity = false;
        this.groupByStoreName = false;
    }

    firstUpdated() {
        if (this.doAPIonFirstUpdate) {
            this.dispatchEvent(
                new CustomEvent('get-stores', {
                    detail: { pageSize: this.pageSize },
                }),
            );
        } else {
            if (!this.stores) {
                return;
            }
            // TODO: should be rewritten
            this.prepareStores();
            this.getStoreLetterList();
        }
    }

    get stores() {
        return this.__stores;
    }

    set stores(newValue) {
        const oldValue = this.__stores;
        this.__stores = newValue;
        this.prepareStores();
        this.getStoreLetterList();
        this.requestUpdate('stores', oldValue);
    }

    prepareStores = () => {
        const groupedStoresList = this.stores.reduce((storesList, store) => {
            if (store && store.address && store.address.town && store.name) {
                const group = this.groupByStoreName
                    ? store.name[0].toUpperCase()
                    : store.address.town[0];
                const city = this.groupByStoreName ? 'all' : store.address.town;
                if (!storesList[group]) {
                    storesList[group] = {
                        group,
                        cities: { [city]: [store] },
                    };
                } else if (storesList[group].cities[city]) {
                    storesList[group].cities[city].push(store);
                } else {
                    storesList[group].cities[city] = [store];
                }
            }
            return storesList;
        }, {});

        this.preparedStores = Object.values(groupedStoresList).sort(function(txtA, txtB) {
            return txtA.group.toUpperCase().localeCompare(txtB.group.toUpperCase());
        });

        this.preparedStores = this.preparedStores.map(el => {
            const sortedKeys = Object.keys(el.cities).sort((a, b) => (a < b ? -1 : a > b ? 1 : 0));
            el.cities = sortedKeys.reduce((acc, key) => {
                return { ...acc, [key]: this.sortStores(this.sortingFields, el.cities[key]) };
            }, {});
            return el;
        });
    };

    getStoreLetterList = () => {
        if (this.preparedStores.length) {
            const storeLettersList = this.preparedStores.map(el => {
                return el.group;
            });

            this.dispatchEvent(
                new CustomEvent('get-store-letters-list', {
                    detail: {
                        storeLettersList,
                    },
                }),
            );
        }
    };

    displayStoreCity(storeCity) {
        if (this.hideStoreCity) {
            return '';
        }

        return html`
            <h3 class="store-alphabet-group__town">
                ${storeCity}
            </h3>
        `;
    }

    simplifiedRender = ({ address = {}, url, name }) => html`
        <div class="store-alphabet-group__link-wrapper">
            <a href="${url}">
                ${this.displayStoreCity(address.town)}
                ${this.storesNotGroupedByCity
                    ? html`
                          <p class="store-alphabet-group__city">
                              <span class="store-alphabet-group__city-town">${address.town}</span>
                              <span class="store-alphabet-group__city-line1">${address.line1}</span>
                          </p>
                      `
                    : html`
                          <p class="store-alphabet-group__name">${name}</p>
                          <p class="text store-alphabet-group__store-address">
                              <span class="store-alphabet-group__line1">
                                  ${address.line1}
                              </span>
                              <span class="store-alphabet-group__line2">
                                  ${address.line2}
                              </span>
                          </p>
                      `}
            </a>
        </div>
    `;

    fullRender(store) {
        //  TODO TECH DEBT rework KV styles
        return html`
            <p class="text text--inline">
                <a
                    href="${store.url}"
                    class="text--uppercase text--bold text--no-decoration text--black"
                >
                    ${store.displayName}
                </a>
                &nbsp; ${store.address.town}
            </p>
            <p class="text">
                ${store.address.line1}
            </p>
            <p class="text">
                ${store.address.postalCode}, ${store.address.town}
            </p>
        `;
    }

    getStore = store => html`
        <div class="store-alphabet-group__details">
            ${this.isSimplified ? this.simplifiedRender(store) : this.fullRender(store)}
        </div>
    `;

    compareStores = (a, b, rule) => {
        const valA = getValueByStorePath(rule, a); // util traverses with reference (rule) as string
        const valB = getValueByStorePath(rule, b); // util traverses with reference (rule) as string
        if (!valA) {
            return -1;
        }
        if (!valB) {
            return 1;
        }
        if (!valA && !valB) {
            return 0;
        }
        return valA > valB ? 1 : valA < valB ? -1 : 0;
    };

    sortStores = (rules, stores) => {
        rules.map(rule => {
            stores.sort((a, b) => this.compareStores(a, b, rule));
        });
        return stores;
    };

    render() {
        return html`
            ${this.preparedStores.map(
                el => html`
                    <div
                        class="store-alphabet-group ${this.isSimplified &&
                            'store-alphabet-stores-simplify'}"
                    >
                        <a id="${el.group}" class="store-alphabet-group__header">${el.group}</a>
                        <ul>
                            ${Object.keys(el.cities).map(
                                city => html`
                                    <li class="store-alphabet-group__item">
                                        <e2-accordion
                                            arrow-open-classes="store-alphabet-group__arrow store-alphabet-group__arrow--open"
                                            arrow-close-classes="store-alphabet-group__arrow store-alphabet-group__arrow--close"
                                            active-xxl
                                            active-xl
                                            active-lg
                                            active-md
                                            active-sm
                                            ?default-open="${this.isSimplified}"
                                        >
                                            ${!this.storesNotGroupedByCity
                                                ? html`
                                                      <h5
                                                          v-slot="title"
                                                          class="store-alphabet-group__title"
                                                      >
                                                          <a
                                                              href="${this.storeLocatorUrl}${city}"
                                                              class="store-alphabet-group__link"
                                                          >
                                                              ${city}
                                                          </a>
                                                      </h5>
                                                  `
                                                : null}

                                            <div v-slot="content">
                                                ${el.cities[city]
                                                    .slice(0, this.amountOfVisibleStoresInCity)
                                                    .map(this.getStore)}
                                            </div>
                                        </e2-accordion>
                                    </li>
                                `,
                            )}
                        </ul>
                    </div>
                `,
            )}
        `;
    }
}

const mapStateToProps = (state, ctx) => {
    const contentReady =
        ctx.__contentReady ||
        !ctx.tabToInitializeContent ||
        ctx.tabToInitializeContent === uiSelectors.getTabName(state);

    if (contentReady && !ctx.__contentReady) {
        ctx.__contentReady = true;
    }

    return {
        stores: contentReady ? selectors.getStores(state) : [],
    };
};

const mapDispatchToEvents = dispatch => ({
    redirect: ({ detail }) => dispatch(globalActions.redirect(detail)),
    'get-store-letters-list': ({ detail }) =>
        dispatch(storeLocatorActions.getStoresLetters(detail.storeLettersList)),
    'get-stores': ({ detail }) => dispatch(storeLocatorActions.getStores(detail)),
});

export default () =>
    customElements.define(
        'e2-store-alphabet-letter-group',
        connect(mapStateToProps, mapDispatchToEvents)(StoreAlphabetLetterGroup),
    );
